import React from 'react';

// When the guest wants to go — Now, Today 12 PM, Tonight 7 PM, Tomorrow 8 AM,
// Tomorrow 12 PM, Tomorrow 7 PM (the two lunch slots are synthesized on the
// page; see LocalIntelligencePage's timeOptions memo). The times are the
// database's, in the park's own timezone; the page never builds one.
//
// work-page-fixes Item 4 — one-row layout: six pills don't fit the panel's
// left column when every one repeats its day word, so the day moves into
// inline TODAY / TOMORROW micro-group labels (the Party/Access pattern from
// PartyChips) and each pill shows only its slot — "Now 7:41 AM", "12 PM",
// "7 PM", "8 AM". The row never wraps: pills are shrink-0 inside a
// flex-nowrap overflow-x-auto strip with the scrollbar hidden, so narrow
// screens scroll instead of stacking. The DATA labels are untouched — they
// drive the query's `at` stamp, the meal/When guardrail and the hint — and
// each button keeps its full data label as the accessible name (aria-label).
// The "Now" pill's clock is the PROPERTY's time (work-page-fixes Item 1):
// the asOf prop is the fake-local Date built on the page from as_of /
// as_of_label, so the pill shows the property's wall time in any browser
// timezone. lucide-react is not a project dependency, so the clock is an
// inline <svg> mirroring the lucide-react shape (the codebase convention; see
// AttachmentList.js / WorkFilters.js).

const ClockIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

// "5:16 PM" — hour with no leading zero, minutes zero-padded, AM/PM. Used by
// displayLabelFor to append the property's current time to the "Now" option.
const fmtClock = (d) => {
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
};

// Circular pill matching the Party/Access chips: rounded-full, filled brand when
// active, bordered neutral otherwise. Auto-width (no w-full) so a row of pills
// sits on one line; centered text keeps them tidy.
const timePill = (active) =>
  `shrink-0 px-3 py-1 text-sm rounded-full border transition-colors whitespace-nowrap ${
    active
      ? 'border-[#32B1FF] bg-[#32B1FF] text-white'
      : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
  }`;

// Display compaction (work-page-fixes Item 4) — see the header comment. The
// pill shows the slot only: the day word is stripped into the inline group
// label rendered beside it ("Today 12 PM" → "12 PM"), and "Now" appends the
// property's clock (Item 1 — asOf is the fake-local Date built on the page
// from as_of / as_of_label, so fmtClock renders the property's wall time in
// any browser; missing prop falls back to the browser clock). Anything the
// day-regexes don't recognize renders verbatim, so an unexpected label never
// breaks.
const displayLabelFor = (label, asOf) => {
  const text = String(label || '');
  if (text.toLowerCase() === 'now') return `Now ${fmtClock(asOf || new Date())}`;
  return text.replace(/^(today|tonight|tomorrow)\s+/i, '');
};

// Which inline day group a label belongs to: Now/Today/Tonight → Today,
// Tomorrow → Tomorrow, anything else → null (no group header rendered).
const groupForLabel = (label) => {
  const text = String(label || '');
  if (/^(now|today|tonight)/i.test(text)) return 'Today';
  if (/^tomorrow/i.test(text)) return 'Tomorrow';
  return null;
};

const TimeChips = ({ options, activeIndex, onChange, asOf }) => {
  const list = Array.isArray(options) ? options : [];
  if (list.length === 0) return null;
  let lastGroup = null;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <ClockIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">When</span>
      </div>
      {/* Responsive (work-page-fixes Item 1 + Item 4): below the sm
          breakpoint the pills wrap to multiple lines so nothing scrolls
          off-screen and the TODAY / TOMORROW labels stay fully visible — each
          label is w-full on mobile, stacking it on its own line above its
          pills. At sm and above the row reverts to a single non-wrapping
          strip with a hidden horizontal scrollbar, matching the original
          desktop layout. The inline TODAY / TOMORROW labels are aria-hidden
          decoration — each button's accessible name is its full data label
          (aria-label), so the radiogroup reads exactly what it did before the
          compaction. */}
      <div
        className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap sm:overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="radiogroup"
        aria-label="When"
      >
        {list.map((opt, i) => {
          const active = activeIndex === i;
          const group = groupForLabel(opt.label);
          const header = group && group !== lastGroup ? group : null;
          lastGroup = group;
          return (
            <React.Fragment key={opt.label}>
              {header && (
                <span
                  aria-hidden="true"
                  className="shrink-0 w-full sm:w-auto text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500"
                >
                  {header}
                </span>
              )}
              <button
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={opt.label}
                onClick={() => onChange(i)}
                className={timePill(active)}
              >
                {displayLabelFor(opt.label, asOf)}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default TimeChips;

